import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  listDocuments,
  createDocumentUploadUrl,
  uploadDocumentToS3,
  getDocumentDownloadUrl,
  deleteDocument,
} from '../services/documents.service';

// The Documents page list. Keyed by sport so a super_admin switching sports refetches cleanly;
// referees/federation_admins pass undefined (their sport is JWT-derived server-side).
export function useDocumentsQuery(sportId?: string) {
  return useQuery({
    queryKey: ['documents', sportId ?? null],
    queryFn: () => listDocuments(sportId),
    staleTime: 60_000,
    // Show the previous filter's rows while the new key loads, instead of dropping to a skeleton.
    // This does NOT make data staler: staleTime and invalidation are untouched, so a mutation
    // elsewhere still refetches and replaces this immediately. It only fills the gap where a key
    // change has no cached entry yet — and `isPlaceholderData` lets the page mark it provisional.
    placeholderData: keepPreviousData,
  });
}

export function useCreateDocumentUploadUrl() {
  return useMutation({ mutationFn: createDocumentUploadUrl });
}

export function useUploadDocumentToS3() {
  return useMutation({
    mutationFn: ({ url, file, contentType }: { url: string; file: File; contentType: string }) =>
      uploadDocumentToS3(url, file, contentType),
  });
}

// Resolve a signed URL on demand (not cached — it's short-lived and single-use per click).
export function useDownloadDocument() {
  return useMutation({ mutationFn: (id: string) => getDocumentDownloadUrl(id) });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteDocument(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}
